/**
 * RSVP Step 5 - Payment method chooser.
 *
 * Reads enabled methods for the event, shows the active tier's price, lets the
 * guest pick Stripe (redirect to hosted checkout) or a manual method (Zelle /
 * Venmo / PayPal) which displays send-to instructions + a "I have paid" button.
 *
 * If the tier price is $0, we auto-skip to "confirmed" (parent decides).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CreditCard, Copy, CheckCircle2, Loader2, Send } from 'lucide-react';
import api from '@/utils/api';
import { usePixels } from '@/contexts/PixelContext';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';
import GoldenTicketApply from '@/components/rsvp/GoldenTicketApply';

const METHOD_ICONS = {
  stripe: '💳',
  zelle: '⚡',
  venmo: '💸',
  paypal: '🅿️',
};

// Sort so Zelle + Venmo appear first, Stripe last (when enabled).
const METHOD_ORDER = { zelle: 1, venmo: 2, paypal: 3, stripe: 9 };
// Per-method accent classes for the visible card (Zelle purple, Venmo blue).
const METHOD_ACCENTS = {
  zelle:  { chip: 'text-purple-700 bg-purple-50 border-purple-200',       button: 'bg-purple-600 hover:bg-purple-700 text-white',   label: 'ZELLE',  fee: 'No fees. Bank-to-bank transfer.' },
  venmo:  { chip: 'text-blue-700 bg-blue-50 border-blue-200',              button: 'bg-blue-600 hover:bg-blue-700 text-white',       label: 'VENMO',  fee: 'No fees when funded from bank or Venmo balance.' },
  paypal: { chip: 'text-indigo-700 bg-indigo-50 border-indigo-200',        button: 'bg-indigo-600 hover:bg-indigo-700 text-white',   label: 'PAYPAL', fee: 'Standard PayPal fees may apply.' },
  stripe: { chip: 'text-neutral-700 bg-neutral-50 border-neutral-200',     button: 'bg-primary-800 hover:bg-primary-900 text-white', label: 'CARD',   fee: 'Instant confirmation. Processing fee applies.' },
};

const fmt = (cents, cur = 'USD') => {
  if (cents == null) return '';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${(cur || '').toUpperCase()}`;
};

const PaymentStep = ({ registrationId, editToken, eventId, eventSlug, eventTitle, onPaymentInitiated, onManualDone }) => {
  const [methods, setMethods] = useState([]);
  const [tier, setTier] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [manualView, setManualView] = useState(null); // { method, handle, reference, instructions, amount_cents }
  const [goldenTicket, setGoldenTicket] = useState(null);

  // Bundle 171 hotfix - effective price after any applied golden ticket.
  const effectivePriceCents = goldenTicket
    ? goldenTicket.final_price_cents
    : (tier ? tier.price_cents : null);

  // Bundle 171 hotfix - commit the applied golden ticket right when
  // the user selects a payment method. Backend commit is idempotent on
  // registration_id so a re-click is safe. Failures here downgrade to
  // a toast; user can still complete payment at the full price if the
  // slot was raced.
  const _commitGoldenTicketIfAny = async () => {
    if (!goldenTicket || !registrationId) return;
    try {
      await api.post('/rsvp/commit-golden-ticket', {
        event_id: eventId,
        code: goldenTicket.code,
        registration_id: registrationId,
        tier_price_cents: tier?.price_cents,
        tier: tier ? { id: tier.id, name: tier.name } : undefined,
      });
    } catch (e) {
      const errCode = e?.response?.data?.detail?.error_code;
      if (errCode === 'CODE_EXHAUSTED') {
        setGoldenTicket(null);
        toast.error('Golden ticket was fully redeemed - price reverted.');
      } else {
        toast.error('Could not apply golden ticket. Continuing at full price.');
        setGoldenTicket(null);
      }
    }
  };
  const [copied, setCopied] = useState('');
  const [txRef, setTxRef] = useState('');
  const [markedPaid, setMarkedPaid] = useState(false);
  const { trackEvent } = usePixels();
  const addToCartFiredRef = useRef(false);
  const initiateCheckoutFiredRef = useRef(new Set());

  useEffect(() => {
    (async () => {
      if (!eventId) return;
      try {
        const { data } = await api.get(`/payments/methods/${eventId}`);
        setMethods(data.methods || []);
        setTier(data.active_tier || null);
        setCurrency(data.currency || 'USD');
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [eventId]);

  const isFree = !tier || (tier.price_cents ?? 0) === 0;

  // Bundle 67 - Funnel semantics fix. Meta Events Manager was reading
  // 9 Purchases / 0 AddToCart / 0 InitiateCheckout because the earlier
  // fires had the semantics swapped and only Stripe was represented on
  // the client. Correct funnel:
  //   AddToCart        = user reached the payment method chooser
  //                       (all methods, regardless of which they'll pick)
  //   InitiateCheckout = user committed to a specific method by clicking
  //                       "Pay with Zelle / Venmo / PayPal / card"
  // Both are mirrored server-side via bufferLogEvent -> /api/pixels/log,
  // which fans out to CAPI with full Advanced Matching (sticky PII cache
  // from Bundle 64).
  useEffect(() => {
    if (loading || isFree || addToCartFiredRef.current) return;
    if (!methods || methods.length === 0) return;
    addToCartFiredRef.current = true;
    trackEvent('AddToCart', {
      content_name: eventTitle || undefined,
      content_ids: [eventSlug || eventId].filter(Boolean),
      content_type: 'event_ticket',
      value: (tier?.price_cents || 0) / 100,
      currency: currency || 'USD',
      num_items: 1,
    });
  }, [loading, isFree, methods, tier, currency, eventTitle, eventSlug, eventId, trackEvent]);

  const goStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        registration_id: registrationId,
        edit_token: editToken,
        origin_url: window.location.origin,
        quantity: 1,
      });
      onPaymentInitiated && onPaymentInitiated({ method: 'stripe', session_id: data.session_id });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not start Stripe checkout.'));
      setBusy(false);
    }
  };

  const openManual = async (method) => {
    setBusy(true);
    try {
      const { data } = await api.post('/payments/manual-instructions', {
        registration_id: registrationId,
        edit_token: editToken,
        method,
      });
      setManualView(data);
      setSelected(method);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not fetch instructions.'));
    } finally { setBusy(false); }
  };

  const copyText = async (label, value) => {
    if (!value) return;
    await navigator.clipboard.writeText(String(value));
    setCopied(label);
    setTimeout(() => setCopied(''), 1400);
  };

  // Bundle 151 - build platform deep-link URLs from the manual
  // instructions payload. Venmo + PayPal have public web schemes
  // that pre-fill amount + memo when opened in a new tab. Zelle
  // does NOT have a public web deep-link so it stays copy-only.
  // Handle sanitization: trim @ on Venmo, allow either paypal.me
  // URL passthrough or bare handle on PayPal.
  const buildDeepLink = (method, view) => {
    if (!view) return null;
    const handle = String(view.handle || '').trim();
    if (!handle) return null;
    const amtUnits = ((view.amount_cents || 0) / 100).toFixed(2);
    const memo = view.memo_string || view.reference || '';
    if (method === 'venmo') {
      const h = handle.replace(/^@/, '');
      return (
        'https://venmo.com/'
        + encodeURIComponent(h)
        + '?txn=pay&amount=' + encodeURIComponent(amtUnits)
        + '&note=' + encodeURIComponent(memo)
      );
    }
    if (method === 'paypal') {
      if (/^https?:\/\/(www\.)?paypal\.me\//i.test(handle)) {
        return handle.replace(/\/+$/, '') + '/' + encodeURIComponent(amtUnits);
      }
      const h = handle.replace(/^@/, '');
      return (
        'https://paypal.me/'
        + encodeURIComponent(h)
        + '/' + encodeURIComponent(amtUnits)
      );
    }
    return null;
  };

  // Bundle 151 - beforeunload guard when guest is mid manual-transfer.
  // Only arms when a manual method is selected and the user has NOT
  // clicked "I have paid" yet. Never blocks; only prompts native
  // browser confirmation.
  useEffect(() => {
    const shouldGuard = !!manualView && !markedPaid;
    const onBeforeUnload = (e) => {
      if (!shouldGuard) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    if (shouldGuard) {
      window.addEventListener('beforeunload', onBeforeUnload);
    }
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [manualView, markedPaid]);

  // Bundle 151 - track whether the guest has interacted with the
  // manual method (opened deep-link OR copied handle). The
  // "I have paid" CTA only enables after interaction so we do not
  // accidentally record a claim from a stray click.
  const [manualInteracted, setManualInteracted] = useState(false);
  const openDeepLink = (method) => {
    const url = buildDeepLink(method, manualView);
    if (!url) return;
    setManualInteracted(true);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const submitMarkedPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/payments/manual-mark-paid', {
        registration_id: registrationId,
        edit_token: editToken,
        method: manualView.method,
        transaction_reference: txRef || null,
        amount_paid_cents: manualView.amount_cents,
      });
      // Bundle 56 - if the profile gate is still pending, route the
      // user straight to it. NEVER show the "Thanks, we got it" panel
      // for a gate-pending user; that misled real users into thinking
      // they were done.
      if (data?.gate_required && data?.registration_id) {
        if (typeof window !== 'undefined') {
          window.location.href = `/rsvp/${data.registration_id}/complete-profile`;
        }
        return;
      }
      setMarkedPaid(true);
      onManualDone && onManualDone({ method: manualView.method });
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to record.'));
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-neutral-500">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" /> Loading payment options...
      </div>
    );
  }

  // Zelle 422 hotfix - defensive guard. `registrationId` is minted by
  // `POST /events/:slug/rsvp` and passed in via the parent. If the
  // parent restored the wizard past the submit step without also
  // restoring `resultUrls` (draft-hydration edge case), we would fire
  // `/payments/manual-instructions` with `registration_id: undefined`
  // and surface a cryptic "Field required" toast. Detect the missing
  // context up front and route the guest back to the form.
  if (!registrationId) {
    return (
      <div
        className="space-y-3 py-10 text-center"
        data-testid="rsvp-payment-session-expired"
      >
        <h3 className="font-display text-2xl font-bold text-primary-900">
          Let&apos;s restart your RSVP.
        </h3>
        <p className="text-sm text-neutral-600 max-w-md mx-auto">
          Your session expired before we saved a payment reference. Please
          resubmit the RSVP form; if you have already paid, contact the host.
        </p>
      </div>
    );
  }

  if (isFree) {
    return (
      <div className="space-y-4" data-testid="rsvp-payment-free">
        <div className="text-center py-6 space-y-3">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-secondary-50 border border-secondary-200">
            <CheckCircle2 className="h-7 w-7 text-secondary-700" />
          </div>
          <h3 className="font-display text-2xl font-bold text-primary-900">Free event</h3>
          <p className="text-neutral-600">No payment required. Your spot is being confirmed.</p>
        </div>
      </div>
    );
  }

  if (markedPaid) {
    return (
      <div className="space-y-4 py-4" data-testid="rsvp-manual-marked-paid">
        <div className="text-center space-y-3">
          <CheckCircle2 className="h-12 w-12 text-success-600 mx-auto" />
          <h3 className="font-display text-2xl font-bold text-primary-900">Thanks - we got it.</h3>
          <p className="text-neutral-600">
            Our team will verify your <strong>{manualView.method}</strong> payment within 24 hours.
            You&apos;ll get an email as soon as your spot is confirmed.
          </p>
        </div>
      </div>
    );
  }

  if (manualView) {
    return (
      <div className="space-y-5" data-testid="rsvp-manual-instructions">
        <div className="rounded-xl border border-secondary-200 bg-secondary-50/70 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-secondary-700">Send</div>
              <div className="font-display text-3xl font-bold text-primary-900">
                {fmt(manualView.amount_cents, manualView.currency)}
              </div>
            </div>
            <div className="text-4xl">{METHOD_ICONS[manualView.method] || '💰'}</div>
          </div>
          <div className="text-sm text-primary-900">via <strong className="capitalize">{manualView.method}</strong></div>
        </div>

        <div className="space-y-3">
          <FieldRow label="Send to"
            value={manualView.handle || manualView.email || manualView.phone}
            onCopy={() => { copyText('handle', manualView.handle || manualView.email || manualView.phone); setManualInteracted(true); }}
            copied={copied === 'handle'}
            testid="manual-handle" />
          {/* Bug Z2: single copy-friendly memo (reference + name + event).
              Backend returns `memo_string` canonicalized; frontend never
              rebuilds this string. */}
          <FieldRow label="Memo (paste this exactly)"
            value={manualView.memo_string || manualView.reference}
            onCopy={() => { copyText('memo', manualView.memo_string || manualView.reference); setManualInteracted(true); }}
            copied={copied === 'memo'}
            testid="manual-memo"
            mono />
        </div>

        {/* Bundle 151 - platform deep-link. Venmo + PayPal open in
            a new tab with amount + memo pre-filled. Zelle has no
            public web scheme, so we render copy-only instructions. */}
        {(manualView.method === 'venmo' || manualView.method === 'paypal') && (
          <div className="rounded-xl border border-secondary-200 bg-white p-4 space-y-2">
            <div className="text-xs uppercase tracking-widest text-secondary-700 font-semibold">
              Open in a new tab
            </div>
            <p className="text-sm text-neutral-700">
              We&apos;ll pre-fill the amount and memo for you. Once you send,
              return to this tab and tap &ldquo;I have paid&rdquo;.
            </p>
            <Button
              onClick={() => openDeepLink(manualView.method)}
              className={`w-full ${manualView.method === 'venmo' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}
              data-testid={`open-${manualView.method}-deep-link-btn`}
            >
              Open {manualView.method === 'venmo' ? 'Venmo' : 'PayPal'} (opens new tab)
            </Button>
          </div>
        )}
        {manualView.method === 'zelle' && (
          <div className="rounded-xl border border-purple-200 bg-white p-4 space-y-2" data-testid="zelle-copy-only-instructions">
            <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">
              Complete in your bank app
            </div>
            <ol className="list-decimal ml-5 space-y-1 text-sm text-neutral-700">
              <li>Open your bank&apos;s Zelle service.</li>
              <li>Send <strong>{fmt(manualView.amount_cents, manualView.currency)}</strong> to <strong>{manualView.handle || manualView.email || manualView.phone}</strong>.</li>
              <li>Use memo <strong className="font-mono">{manualView.memo_string || manualView.reference}</strong>.</li>
              <li>Return here and click <strong>I have paid</strong>.</li>
            </ol>
          </div>
        )}

        <div className="text-xs text-neutral-700 bg-secondary-50 border border-secondary-200 rounded-lg p-3 leading-relaxed">
          <div className="font-semibold text-primary-900 mb-1">How to pay</div>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Open <strong className="capitalize">{manualView.method}</strong> and start a payment to the handle above.</li>
            <li>Paste the <strong>memo</strong> exactly as shown - this is how we auto-match your payment.</li>
            <li>Send the amount shown, then tap <strong>&ldquo;I have paid&rdquo;</strong> below.</li>
          </ol>
          {manualView.instructions && (
            <div className="mt-2 pt-2 border-t border-secondary-200 text-neutral-600">
              {manualView.instructions}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Your transaction ID / confirmation (optional)</Label>
          <Input
            value={txRef}
            onChange={(e) => setTxRef(e.target.value)}
            placeholder="e.g. Zelle confirmation number"
            data-testid="manual-txid-input"
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManualView(null)} disabled={busy}>Back</Button>
          <Button
            onClick={submitMarkedPaid}
            disabled={busy || !manualInteracted}
            title={manualInteracted ? '' : 'Open the payment app or copy the handle first.'}
            className="flex-1 bg-primary-800 hover:bg-primary-900 text-white disabled:opacity-60"
            data-testid="manual-mark-paid-btn"
          >
            <Send className="h-4 w-4 mr-1.5" /> I have paid
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="rsvp-payment-step">
      <div className="rounded-xl border border-primary-100 bg-primary-50/40 p-5 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary-700 mb-1">Your ticket</div>
          <div className="font-semibold text-primary-900">{tier.name || 'Ticket'}</div>
        </div>
        <div className="text-right">
          {goldenTicket ? (
            <div
              className="text-xs text-neutral-500 line-through"
              data-testid="rsvp-price-original"
            >
              {fmt(tier.price_cents, currency)}
            </div>
          ) : null}
          <div
            className="font-display text-2xl font-bold text-primary-900"
            data-testid="rsvp-price-final"
          >
            {fmt(effectivePriceCents, currency)}
          </div>
          {goldenTicket ? (
            <div
              className="text-[10px] uppercase tracking-widest text-secondary-800 mt-0.5"
              data-testid="rsvp-price-discount-line"
            >
              Golden ticket: -{fmt(goldenTicket.discount_cents, currency)}
            </div>
          ) : null}
        </div>
      </div>

      <GoldenTicketApply
        eventId={eventId}
        tierPriceCents={tier.price_cents}
        applied={goldenTicket}
        onApplied={setGoldenTicket}
        currency={currency}
      />

      <div className="grid gap-3">
        {[...methods].sort((a, b) => (METHOD_ORDER[a.method] || 5) - (METHOD_ORDER[b.method] || 5)).map((m) => {
          const accent = METHOD_ACCENTS[m.method] || METHOD_ACCENTS.stripe;
          return (
            <div
              key={m.method}
              className={`rounded-xl border-2 p-5 transition-all
                ${selected === m.method ? 'border-secondary-500 bg-secondary-50/40' : 'border-neutral-200 hover:border-neutral-300'}`}
              data-testid={`payment-method-${m.method}-card`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-block text-[10px] uppercase tracking-widest border rounded px-1.5 py-0.5 font-bold ${accent.chip}`}>
                      {accent.label}
                    </span>
                    {m.method !== 'stripe' && (
                      <span className="text-[10px] uppercase tracking-widest text-secondary-800 font-semibold">
                        Zero fees
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-primary-900 text-base">{m.display_name || m.method.toUpperCase()}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{accent.fee}</div>
                  <div className="text-lg font-display font-bold text-primary-900 mt-2">
                    {fmt(effectivePriceCents, currency)}
                  </div>
                </div>
                <Button
                  onClick={() => {
                    _commitGoldenTicketIfAny();
                    setSelected(m.method);
                    // Bundle 67 - InitiateCheckout on method-button click.
                    // Once per (method) per mount so a user hovering
                    // Zelle then Venmo does not double-fire the same
                    // method. Server-side dedup is via deterministic
                    // event_id (session:name:minute) inside pixel_log.
                    if (!initiateCheckoutFiredRef.current.has(m.method)) {
                      initiateCheckoutFiredRef.current.add(m.method);
                      trackEvent('InitiateCheckout', {
                        content_name: eventTitle || undefined,
                        content_ids: [eventSlug || eventId].filter(Boolean),
                        content_type: 'event_ticket',
                        value: (tier?.price_cents || 0) / 100,
                        currency: currency || 'USD',
                        num_items: 1,
                        payment_method: m.method,
                      });
                    }
                    if (m.method === 'stripe') goStripe();
                    else openManual(m.method);
                  }}
                  disabled={busy}
                  className={accent.button}
                  data-testid={`payment-method-${m.method}-btn`}
                >
                  {busy && selected === m.method
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    : null}
                  Pay with {m.method === 'stripe' ? 'card' : (m.display_name || m.method).replace(/^(Zelle|Venmo|PayPal).*/, '$1')}
                </Button>
              </div>
            </div>
          );
        })}
        {methods.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-accent-200 bg-accent-50 p-5 text-sm text-accent-800" data-testid="rsvp-payment-none">
            No payment methods are enabled for this event yet. Please contact the host at
            <a className="underline mx-1" href="mailto:yaar@dostination.com">yaar@dostination.com</a>
            to complete your RSVP.
          </div>
        )}
      </div>
      <p className="text-xs text-neutral-500 text-center">
        Refund policy per the event page. All transactions are secure.
      </p>
    </div>
  );
};

const FieldRow = ({ label, value, onCopy, copied, testid, mono = false }) => (
  <div className="border border-neutral-100 rounded-lg p-3 flex items-center gap-3">
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
      <div className={`text-sm text-primary-900 truncate ${mono ? 'font-mono font-bold text-secondary-700' : 'font-semibold'}`} data-testid={testid}>
        {value || '-'}
      </div>
    </div>
    <button onClick={onCopy} className="flex items-center gap-1 text-xs text-primary-700 hover:text-primary-900 shrink-0" data-testid={`${testid}-copy`}>
      {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  </div>
);

export default PaymentStep;
